/**
 * `/raporty/katalog` — the trainer's own question shelf, as KANON `06-katalog-pytan.html` draws it.
 *
 * A header with the way back, a toolbar with the search and two filters, and one card of rows. The
 * rows say what a question asks, what kind of answer it takes, when it was saved and which surveys
 * hold a copy; the pencil corrects it and the trash removes it from the shelf. Both are bare glyphs
 * — `canon.ibtnBare` — since 17 September 2026, and the trash carries the danger ink: the owner
 * asked for the two tiles they stood on to go.
 *
 * ── The one thing this screen exists to make clear ───────────────────────────────────────────
 * A shelf entry is a stencil. Deleting it does not touch a survey that already uses it, because the
 * survey holds its own copy (R12, R35) — the lede says so and the delete dialog says it again with
 * the number of surveys in hand. Without that, the trash reads as „delete this question everywhere",
 * which is the opposite of what it does.
 *
 * ── Loading without movement ─────────────────────────────────────────────────────────────────
 * The header and the toolbar render in every state; the card holds `--h-report-picker-list` before
 * its read lands, and the skeleton stands inside it. The count beside the filters is the TOTAL the
 * shelf holds, printed once the read answers — it sits in a fixed track so it cannot move the
 * toolbar when it arrives.
 */

import { motion } from 'motion/react';
import { useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { screenMotion, useScreenMotionPreference } from '~/shared/design-system/motion';
import { useDocumentTitle } from '~/shared/lib/useDocumentTitle';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { EmptyState } from '~/shared/ui/EmptyState';
import { InboxMarkIcon, NoteMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { ScreenHeaderPlus } from '~/shared/ui/ScreenHeader';
import header from '~/shared/ui/screenHeader.module.css';
import { SearchField } from '~/shared/ui/SearchField';
import ember from '~/shared/ui/emberButton.module.css';
import ghost from '~/shared/ui/ghostButton.module.css';
import stateAction from '~/shared/ui/stateAction.module.css';
import iconButton from '~/shared/ui/iconButton.module.css';
import shell from '~/shared/ui/canonScreen.module.css';
import skel from '~/shared/ui/skeleton.module.css';

import {
  answerTypeIcon,
  answerTypeWords,
  shelfDeletePrompt,
  ShelfQuestionDialog,
  useQuestionShelf,
  type ShelfDraft,
  type ShelfQuestion,
} from '~/features/survey-question-editor';
import { BackIcon, PencilIcon, TrashIcon } from '~/shared/ui/ReportIcons';
import { ShelfFilters } from './ShelfFilters';
import canon from '~/shared/ui/surveyCanon.module.css';
import styles from './QuestionShelf.module.css';

/** How many skeleton rows the card draws. Five is the canon's own list, less one. */
const SKELETON_ROWS = 5;

export function QuestionShelfScreen() {
  const { t, i18n } = useTranslation();
  const reducedMotion = useScreenMotionPreference();
  const shelf = useQuestionShelf();
  const [editing, setEditing] = useState<ShelfQuestion | 'new' | null>(null);
  const [deleting, setDeleting] = useState<ShelfQuestion | null>(null);
  const newRef = useRef<HTMLButtonElement>(null);

  useDocumentTitle(t('web:reports.catalog.title'));

  // The saved date, in the locale the app is running in. `Intl` directly — browsers carry the data
  // this app's phone sibling has to ship tables for.
  const savedOn = (iso: string | null): string =>
    iso === null
      ? ''
      : new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' }).format(
          new Date(iso),
        );

  const submit = (draft: ShelfDraft, id: number | null): void => {
    const done = (): void => {
      setEditing(null);
    };
    if (id === null) {
      shelf.create(draft, done);
      return;
    }
    shelf.update(id, draft, done);
  };

  return (
    <main className={shell.page}>
      <motion.div className={shell.measure} {...screenMotion('rise', { reducedMotion })}>
        <div className={styles.header}>
          <div className={styles.headText}>
            <span className={styles.crumb}>
              <Link
                to="/raporty"
                className={`${String(iconButton.iconButton)} ${String(styles.crumbLink)}`}
                aria-label={t('web:reports.editor.back')}
              >
                <BackIcon className={styles.crumbIcon} />
              </Link>
              {t('web:reports.title')}
            </span>
            {/* `AppShell` moves focus to the screen's `h1` after a route change, so it renders in
              * every state rather than waiting for a read. */}
            <h1 className={`${String(canon.h1)} ${String(styles.title)}`}>
              {t('web:reports.catalog.title')}
            </h1>
            <p className={styles.lede}>{t('web:reports.catalog.lede')}</p>
          </div>
          <button
            ref={newRef}
            type="button"
            className={`${String(ember.ember)} ${String(header.primary)}`}
            aria-label={t('web:reports.catalog.new')}
            title={t('web:reports.catalog.new')}
            onClick={() => {
              setEditing('new');
            }}
          >
            <ScreenHeaderPlus className={header.primaryIcon} />
          </button>
        </div>

        <div className={styles.tools}>
          <SearchField
            className={styles.search}
            value={shelf.facets.term}
            onChange={(term) => {
              shelf.setFacets({ ...shelf.facets, term });
            }}
            label={t('web:reports.catalog.searchLabel')}
            placeholder={t('web:reports.catalog.searchPlaceholder')}
            clearLabel={t('web:reports.catalog.searchClear')}
            disabled={shelf.state === 'pending' || shelf.state === 'error'}
          />
          <ShelfFilters
            facets={shelf.facets}
            answerTypes={shelf.answerTypes}
            onChange={shelf.setFacets}
            t={t}
          />
          <span className={styles.toolSpacer} />
          <span className={styles.count}>
            {shelf.state === 'pending' || shelf.state === 'error'
              ? ''
              : t('web:reports.catalog.count', { count: shelf.total })}
          </span>
        </div>

        {shelf.writeFailed ? (
          <p className={styles.banner} role="alert">
            {t('web:reports.catalog.writeFailed')}
          </p>
        ) : null}

        <div className={styles.card}>
          {shelf.state === 'pending' ? (
            <div aria-hidden={!shelf.skeleton}>
              {Array.from({ length: SKELETON_ROWS }, (_, row) => (
                <div key={row} className={styles.skelRow}>
                  {shelf.skeleton ? (
                    <i className={`${String(skel.bar)} ${String(styles.skelBar)}`} />
                  ) : null}
                </div>
              ))}
            </div>
          ) : shelf.state === 'error' ? (
            <div className={styles.state}>
              <EmptyState
                mark={<NoteMarkIcon />}
                tone="neutral"
                title={t('web:reports.catalog.states.errorTitle')}
                hint={t('web:reports.catalog.states.errorMessage')}
              />
              {/* The promoted retry, the same one the hub's four surfaces draw. `onRetry` was on the
                * hook from the first commit and nothing called it, so this state was a dead end. */}
              <button
                type="button"
                className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}
                onClick={shelf.onRetry}
              >
                {t('web:reports.surveys.states.retry')}
              </button>
            </div>
          ) : shelf.state === 'rows' ? (
            <ul className={`${String(styles.list)} ${shelf.reveal}`}>
              {shelf.rows.map((question) => (
                <li key={question.id} className={styles.row}>
                  <span className={styles.tile}>{answerTypeIcon(question.answerType, styles.tileIcon)}</span>
                  <span className={styles.rowText}>
                    <span className={styles.rowLabel}>{question.label}</span>
                    <span className={styles.rowMeta}>
                      {answerTypeWords(
                        { answerType: question.answerType, optionCount: question.options.length },
                        t,
                      )}
                      <span>·</span>
                      <span>{t('web:reports.catalog.savedOn', { date: savedOn(question.savedAt) })}</span>
                    </span>
                  </span>
                  <span className={styles.tags}>
                    {question.usedIn.length === 0 ? (
                      <span className={styles.unused}>{t('web:reports.catalog.unused')}</span>
                    ) : (
                      question.usedIn.map((usage) => (
                        <span key={usage.templateId} className={styles.tag}>
                          {usage.title}
                        </span>
                      ))
                    )}
                  </span>
                  {/* Bare glyphs, no tile: the canon's icon button with its edge taken away. */}
                  <button
                    type="button"
                    className={`${String(canon.ibtn)} ${String(canon.ibtnBare)}`}
                    aria-label={t('web:reports.catalog.edit', { label: question.label })}
                    onClick={() => {
                      setEditing(question);
                    }}
                  >
                    <PencilIcon className={canon.ibtnIconMd} />
                  </button>
                  <button
                    type="button"
                    className={`${String(canon.ibtn)} ${String(canon.ibtnBare)} ${String(canon.ibtnDanger)}`}
                    aria-label={t('web:reports.catalog.remove', { label: question.label })}
                    onClick={() => {
                      setDeleting(question);
                    }}
                  >
                    <TrashIcon className={canon.ibtnIconMd} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className={styles.state}>
              <EmptyState
                mark={<InboxMarkIcon />}
                tone="positive"
                title={
                  shelf.state === 'noResults'
                    ? t('web:reports.catalog.noResults.title')
                    : t('web:reports.catalog.empty.title')
                }
                hint={
                  shelf.state === 'noResults'
                    ? t('web:reports.catalog.noResults.message')
                    : t('web:reports.catalog.empty.message')
                }
              />
            </div>
          )}
        </div>
      </motion.div>

      <ShelfQuestionDialog
        editing={editing}
        busy={shelf.saving}
        onSubmit={submit}
        onClose={() => {
          setEditing(null);
        }}
        triggerRef={newRef}
        t={t}
      />

      <ConfirmDialog
        prompt={deleting === null ? null : shelfDeletePrompt(deleting, t, shelf.saving)}
        onConfirm={() => {
          if (deleting === null) return;
          shelf.remove(deleting.id, () => {
            setDeleting(null);
          });
        }}
        onCancel={() => {
          setDeleting(null);
        }}
      />
    </main>
  );
}
